import {
  ChangeOfHostedFamilyHostedFamilyFollowUpDto,
  ShiftHostedFamilyFollowUpDto,
} from '../../../../interactors/gen/backendClient'
import { HostedFamilyFollowUpDtoType } from '../../../../domain/Animal/HostedFamilyFollowUpDtoType'
import dayjs from 'dayjs'

export type MainHostedFamilyFollowUp = ChangeOfHostedFamilyHostedFamilyFollowUpDto | ShiftHostedFamilyFollowUpDto

function isChange(fu: MainHostedFamilyFollowUp): fu is ChangeOfHostedFamilyHostedFamilyFollowUpDto {
  return fu.type === HostedFamilyFollowUpDtoType.ChangeOfHostedFamily
}

/**
 * Sort follow-ups chronologically
 */
function sortFollowUps(followUps: MainHostedFamilyFollowUp[]): MainHostedFamilyFollowUp[] {
  return [...followUps].sort((a, b) => {
    const da = dayjs(a.date, 'DD/MM/YYYY')
    const db = dayjs(b.date, 'DD/MM/YYYY')
    if (da.isBefore(db)) return -1
    if (da.isAfter(db)) return 1
    // tie-breaker on created.at
    const ca = dayjs(a.created.at)
    const cb = dayjs(b.created.at)
    return ca.isBefore(cb) ? -1 : ca.isAfter(cb) ? 1 : 0
  })
}

/**
 * Get the destination/location from a follow-up
 */
function getDestinationFromFollowUp(followUp: MainHostedFamilyFollowUp): string | undefined {
  if (isChange(followUp)) {
    return followUp.afterAccountId
  } else {
    return followUp.destination
  }
}

/**
 * Get the source/origin from a follow-up
 */
function getSourceFromFollowUp(followUp: MainHostedFamilyFollowUp): string | undefined {
  if (isChange(followUp)) {
    return followUp.beforeAccountId
  } else {
    // For shift/initial placement, we need to look at the previous follow-up's destination
    return undefined
  }
}

/**
 * Update a follow-up's source/origin
 */
function updateFollowUpSource(
  followUp: MainHostedFamilyFollowUp,
  newSource: string | undefined
): MainHostedFamilyFollowUp {
  if (isChange(followUp)) {
    return {
      ...followUp,
      beforeAccountId: newSource || '',
    }
  } else {
    // For shifts, the source is implicit from the previous follow-up
    return followUp
  }
}

/**
 * Relink the chain after an edit or deletion
 */
function relinkChain(sortedFollowUps: MainHostedFamilyFollowUp[]): MainHostedFamilyFollowUp[] {
  const result = [...sortedFollowUps]

  for (let i = 0; i < result.length; i++) {
    const current = result[i]
    const previous = result[i - 1]
    const next = result[i + 1]

    // Skip initial placement - it doesn't need linking
    if (current.type === HostedFamilyFollowUpDtoType.InitialPlacement) {
      continue
    }

    // Link current follow-up's source to previous follow-up's destination
    if (previous) {
      const previousDestination = getDestinationFromFollowUp(previous)
      result[i] = updateFollowUpSource(current, previousDestination)
    }

    // Link next follow-up's source to current follow-up's destination
    if (next) {
      const currentDestination = getDestinationFromFollowUp(result[i])
      result[i + 1] = updateFollowUpSource(next, currentDestination)
    }
  }

  return result
}

/**
 * Replace one edited follow-up and then re-link its neighbors.
 */
export function updateAdjacentFollowUps(
  all: MainHostedFamilyFollowUp[],
  edited: MainHostedFamilyFollowUp
): MainHostedFamilyFollowUp[] {
  // 1) sort chronologically
  const sorted = sortFollowUps(all)

  // 2) find & replace
  const idx = sorted.findIndex((fu) => fu.created.at === edited.created.at)
  if (idx === -1) return sorted
  sorted[idx] = edited

  // 3) relink the entire chain to ensure consistency
  return relinkChain(sorted)
}

/**
 * Remove a follow-up and relink the remaining chain
 */
export function removeFollowUpAndRelink(
  all: MainHostedFamilyFollowUp[],
  toRemove: MainHostedFamilyFollowUp
): MainHostedFamilyFollowUp[] {
  // 1) sort chronologically
  const sorted = sortFollowUps(all)

  // 2) find the follow-up to remove
  const removeIdx = sorted.findIndex((fu) => fu.created.at === toRemove.created.at)
  if (removeIdx === -1) return sorted

  // 3) Don't allow removal of initial placement
  if (sorted[removeIdx].type === HostedFamilyFollowUpDtoType.InitialPlacement) {
    throw new Error('Cannot remove initial placement follow-up')
  }

  // 4) remove the follow-up
  const remaining = sorted.filter((_, idx) => idx !== removeIdx)

  // 5) relink the chain
  return relinkChain(remaining)
}

/**
 * Validate that the follow-up chain is consistent
 */
export function validateFollowUpChain(followUps: MainHostedFamilyFollowUp[]): string[] {
  const errors: string[] = []
  const sorted = sortFollowUps(followUps)

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i]
    const previous = sorted[i - 1]

    if (current.type === HostedFamilyFollowUpDtoType.InitialPlacement) {
      errors.push(`Initial placement should be the first follow-up (found at position ${i + 1})`)
      continue
    }

    const previousDestination = getDestinationFromFollowUp(previous)
    const currentSource = getSourceFromFollowUp(current)

    // Only validate source linking for ChangeOfHostedFamily types
    if (isChange(current) && previousDestination !== currentSource) {
      errors.push(
        `Chain break at position ${
          i + 1
        }: previous destination (${previousDestination}) doesn't match current source (${currentSource})`
      )
    }
  }

  return errors
}
